import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import EditIcon from "@material-ui/icons/Edit";
import { AccountCircle } from "@material-ui/icons";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import whatsappIcon from "../../assets/nopicture.png";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import {
  SocketContext,
  socketManager,
} from "../../context/Socket/SocketContext";
import UserStatusIcon from "../../components/UserModal/statusIcon";
import { getBackendUrl } from "../../config";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, FormControlLabel, RadioGroup, Radio, Typography,
  Select, MenuItem as MuiMenuItem, InputLabel,
  Chip, Tooltip } from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import ForbiddenPage from "../../components/ForbiddenPage";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import { useHistory } from "react-router-dom";
import usePlans from "../../hooks/usePlans";

const backendUrl = getBackendUrl();

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "UPDATE_USER_STATUS") {
    const { userId, online } = action.payload;
    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state[userIndex] = { ...state[userIndex], online };
      return [...state];
    }
    return state;
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  userAvatar: {
    width: theme.spacing(6),
    height: theme.spacing(6),
  },
  avatarDiv: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
  },
  loadingText: {
    marginLeft: theme.spacing(2),
  },
}));

const Users = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [transferTicketsModalOpen, setTransferTicketsModalOpen] = useState(false);
  const [transferringTicketsUser, setTransferringTicketsUser] = useState(null);
  const [transferTicketsAction, setTransferTicketsAction] = useState("pending");
  const [transferTicketsTargetId, setTransferTicketsTargetId] = useState("");
  const [deleteAction, setDeleteAction] = useState("pending"); // "pending" | "transfer"
  const [transferToUserId, setTransferToUserId] = useState("");
  const [otherUsers, setOtherUsers] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);
  const { user: loggedInUser, socket } = useContext(AuthContext);
  const { profileImage } = loggedInUser;
  const companyId = loggedInUser.companyId;

  const [showInternalChat, setShowInternalChat] = useState(false);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      if (!companyId) {
        return;
      }

      try {
        setLoading(true);
        const planConfigs = await getPlanCompany(undefined, companyId);

        setShowInternalChat(planConfigs.plan.useInternalChat);
      } catch (err) {
        console.error("Error fetching plan:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam, pageNumber },
        });
        dispatch({ type: "LOAD_USERS", payload: data.users });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };
    fetchUsers();
  }, [searchParam, pageNumber]);

useEffect(() => {
  if (loggedInUser) {
    const companyId = loggedInUser.companyId;
    
    const onCompanyUser = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    };
    
    socket.on(`company-${companyId}-user`, onCompanyUser);

    const onUserOnline = ({ userId }) => {
      dispatch({ type: "UPDATE_USER_STATUS", payload: { userId, online: true } });
    };
    const onUserOffline = ({ userId }) => {
      dispatch({ type: "UPDATE_USER_STATUS", payload: { userId, online: false } });
    };
    socket.on("user:online", onUserOnline);
    socket.on("user:offline", onUserOffline);

    return () => {
      socket.off(`company-${companyId}-user`, onCompanyUser);
      socket.off("user:online", onUserOnline);
      socket.off("user:offline", onUserOffline);
    };
  }
}, [socket, loggedInUser]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const openDeleteModal = async (user) => {
    setDeletingUser(user);
    setDeleteAction("pending");
    setTransferToUserId("");
    // Load other users for transfer option
    try {
      const { data } = await api.get("/users/", { params: { pageNumber: 1 } });
      setOtherUsers((data.users || []).filter(u => u.id !== user.id));
    } catch (e) { setOtherUsers([]); }
    setDeleteModalOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    if (deleteAction === "transfer" && !transferToUserId) {
      toast.error("Selecione o usuário para transferir os tickets.");
      return;
    }
    try {
      await api.delete(`/users/${deletingUser.id}`, {
        data: {
          transferToUserId: deleteAction === "transfer" ? transferToUserId : null
        }
      });
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeleteModalOpen(false);
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const openTransferTicketsModal = async (user) => {
    setTransferringTicketsUser(user);
    setTransferTicketsAction("pending");
    setTransferTicketsTargetId("");
    try {
      const { data } = await api.get("/users/", { params: { pageNumber: 1 } });
      setOtherUsers((data.users || []).filter(u => u.id !== user.id));
    } catch (e) { setOtherUsers([]); }
    setTransferTicketsModalOpen(true);
  };

  const handleTransferTickets = async () => {
    if (!transferringTicketsUser) return;
    if (transferTicketsAction === "transfer" && !transferTicketsTargetId) {
      toast.error("Selecione o atendente de destino.");
      return;
    }
    try {
      const { data } = await api.post(`/users/${transferringTicketsUser.id}/transfer-tickets`, {
        targetUserId: transferTicketsAction === "transfer" ? transferTicketsTargetId : null
      });
      const n = data.transferred || 0;
      if (n === 0) {
        toast.info("Nenhum ticket ativo encontrado para este atendente.");
      } else {
        toast.success(`${n} ticket${n === 1 ? "" : "s"} transferido${n === 1 ? "" : "s"} para Aguardando com sucesso!`);
      }
    } catch (err) {
      toastError(err);
    }
    setTransferTicketsModalOpen(false);
    setTransferringTicketsUser(null);
  };

  const loadMore = () => {
    setLoadingMore(true);
    setPageNumber((prevPage) => prevPage + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

const renderProfileImage = (user) => {
  const buildImageUrl = (userData) => {
    if (!userData.profileImage) {
      return whatsappIcon;
    }
    return `${backendUrl}/public/company${userData.companyId}/user/${userData.profileImage}`;
  };

  // Para o usuário logado, verificar localStorage também
  if (user.id === loggedInUser.id) {
    const savedProfileImage = localStorage.getItem("profileImage");
    const profileImageToUse = savedProfileImage || user.profileImage;
    
    const imageUrl = profileImageToUse 
      ? `${backendUrl}/public/company${user.companyId}/user/${profileImageToUse}`
      : whatsappIcon;

    return (
      <Avatar
        src={imageUrl}
        alt={user.name}
        className={classes.userAvatar}
        onError={(e) => {
          e.target.src = whatsappIcon;
        }}
      />
    );
  }
  
  // Para outros usuários
  return (
    <Avatar
      src={buildImageUrl(user)}
      alt={user.name}
      className={classes.userAvatar}
    />
  );
};

  const handleCreateChat = async (targetUser) => {
    try {
      setLoading(true);
      const { data } = await api.post("/chats", {
        users: [{ id: loggedInUser.id }, { id: targetUser.id }],
        isGroup: false,
        title: targetUser.name,
      });
      toast.success("Chat criado com sucesso!");
      history.push(`/chats/${data.uuid}`);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackfillChats = async () => {
    if (
      !window.confirm(
        "Tem certeza que deseja gerar chats para todos os usuários existentes? Isso pode demorar e criar muitos chats."
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      await api.post("/chats/backfill");
      toast.success("Chats gerados com sucesso!");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainContainer>
      {/* Transfer Tickets Modal */}
      <Dialog open={transferTicketsModalOpen} onClose={() => setTransferTicketsModalOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ style: { borderRadius: 12 } }}>
        <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <SwapHorizIcon style={{ color: "#3b82f6" }} />
          <span style={{ fontWeight: 700 }}>Transferir tickets: {transferringTicketsUser?.name}</span>
        </DialogTitle>
        <DialogContent dividers>
          <Typography style={{ fontSize: "0.88rem", marginBottom: 16, color: "#555" }}>
            Para onde deseja mover os tickets ativos deste atendente?
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup value={transferTicketsAction} onChange={e => setTransferTicketsAction(e.target.value)}>
              <FormControlLabel
                value="pending"
                control={<Radio color="primary" />}
                label={<span style={{ fontSize: "0.88rem" }}>Mover para <strong>Aguardando</strong> (sem atendente)</span>}
              />
              <FormControlLabel
                value="transfer"
                control={<Radio color="primary" />}
                label={<span style={{ fontSize: "0.88rem" }}>Transferir para outro atendente</span>}
              />
            </RadioGroup>
          </FormControl>
          {transferTicketsAction === "transfer" && (
            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: 12 }}>
              <InputLabel>Selecionar atendente</InputLabel>
              <Select
                value={transferTicketsTargetId}
                onChange={e => setTransferTicketsTargetId(e.target.value)}
                label="Selecionar atendente"
              >
                {otherUsers.map(u => (
                  <MuiMenuItem key={u.id} value={u.id}>
                    {u.name} {u.profile === "admin" ? "(admin)" : ""}
                  </MuiMenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button onClick={() => setTransferTicketsModalOpen(false)} variant="outlined"
            style={{ borderRadius: 8, textTransform: "none" }}>Cancelar</Button>
          <Button onClick={handleTransferTickets} variant="contained"
            style={{ borderRadius: 8, textTransform: "none", fontWeight: 700,
              background: "#3b82f6", color: "#fff" }}>
            Transferir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Modal com opções */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ style: { borderRadius: 12 } }}>
        <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <WarningIcon style={{ color: "#f59e0b" }} />
          <span style={{ fontWeight: 700 }}>Excluir usuário: {deletingUser?.name}</span>
        </DialogTitle>
        <DialogContent dividers>
          <Typography style={{ fontSize: "0.88rem", marginBottom: 16, color: "#555" }}>
            Este usuário possui conversas abertas. O que deseja fazer com elas?
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup value={deleteAction} onChange={e => setDeleteAction(e.target.value)}>
              <FormControlLabel
                value="pending"
                control={<Radio color="primary" />}
                label={<span style={{ fontSize: "0.88rem" }}>Mover todas para <strong>Aguardando</strong> (sem atendente)</span>}
              />
              <FormControlLabel
                value="transfer"
                control={<Radio color="primary" />}
                label={<span style={{ fontSize: "0.88rem" }}>Transferir para outro atendente</span>}
              />
            </RadioGroup>
          </FormControl>
          {deleteAction === "transfer" && (
            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: 12 }}>
              <InputLabel>Selecionar atendente</InputLabel>
              <Select
                value={transferToUserId}
                onChange={e => setTransferToUserId(e.target.value)}
                label="Selecionar atendente"
              >
                {otherUsers.map(u => (
                  <MuiMenuItem key={u.id} value={u.id}>
                    {u.name} {u.profile === "admin" ? "(admin)" : ""}
                  </MuiMenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Typography style={{ fontSize: "0.78rem", color: "#999", marginTop: 14 }}>
            O usuário será excluído permanentemente do sistema após confirmar.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button onClick={() => setDeleteModalOpen(false)} variant="outlined"
            style={{ borderRadius: 8, textTransform: "none" }}>Cancelar</Button>
          <Button onClick={handleDeleteUser} variant="contained"
            style={{ borderRadius: 8, textTransform: "none", fontWeight: 700,
              background: "#ef4444", color: "#fff" }}>
            Excluir Usuário
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${
            deletingUser.name
          }?`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />
      {loggedInUser.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Title>
              {i18n.t("users.title")} ({users.length})
            </Title>
            <MainHeaderButtonsWrapper>
              <TextField
                placeholder={i18n.t("contacts.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "gray" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenUserModal}
              >
                {i18n.t("users.buttons.add")}
              </Button>
              {loggedInUser.profile === "admin" && showInternalChat && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleBackfillChats}
                  disabled={loading}
                >
                  Gerar Chats Existentes
                </Button>
              )}
            </MainHeaderButtonsWrapper>
          </MainHeader>
          <Paper
            className={classes.mainPaper}
            variant="outlined"
            onScroll={handleScroll}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">
                    {i18n.t("users.table.ID")}
                  </TableCell>
                  <TableCell align="center">
                    {i18n.t("users.table.status")}
                  </TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">
                    {i18n.t("users.table.name")}
                  </TableCell>
                  <TableCell align="center">
                    {i18n.t("users.table.email")}
                  </TableCell>
                  <TableCell align="center">
                    {i18n.t("users.table.profile")}
                  </TableCell>
                  <TableCell align="center">Filas</TableCell>
                  <TableCell align="center">Conexões</TableCell>
                  <TableCell align="center">
                    {i18n.t("users.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <>
                  {(() => {
                    // Group users by first queue
                    const groups = [];
                    const groupMap = {};
                    const noQueue = { id: "__none__", name: "Sem Fila", color: "#9e9e9e", users: [] };
                    users.forEach(u => {
                      const q = u.queues && u.queues.length > 0 ? u.queues[0] : null;
                      if (!q) { noQueue.users.push(u); return; }
                      if (!groupMap[q.id]) {
                        groupMap[q.id] = { id: q.id, name: q.name, color: q.color || "#9e9e9e", users: [] };
                        groups.push(groupMap[q.id]);
                      }
                      groupMap[q.id].users.push(u);
                    });
                    if (noQueue.users.length > 0) groups.push(noQueue);
                    return groups.map(group => (
                      <React.Fragment key={group.id}>
                        {group.users.map(user => (
                    <TableRow key={user.id}>
                      <TableCell align="center">{user.id}</TableCell>
                      <TableCell align="center">
                        <UserStatusIcon user={user} />
                      </TableCell>
                      <TableCell align="center">
                        <div className={classes.avatarDiv}>
                          {renderProfileImage(user)}
                        </div>
                      </TableCell>
                      <TableCell align="center">{user.name}</TableCell>
                      <TableCell align="center">{user.email}</TableCell>
                      <TableCell align="center">{user.profile}</TableCell>
                      <TableCell align="center">
                        {(() => {
                          const qs = user.queues || [];
                          const visible = qs.slice(0, 2);
                          const extra = qs.length - 2;
                          return (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "center" }}>
                              {visible.map(q => (
                                <Chip key={q.id} label={q.name} size="small"
                                  style={{ backgroundColor: q.color || "#e0e0e0", color: "#fff", fontSize: "0.68rem", height: 20 }} />
                              ))}
                              {extra > 0 && (
                                <Tooltip title={qs.slice(2).map(q => q.name).join(", ")}>
                                  <Chip label={`+${extra}`} size="small"
                                    style={{ fontSize: "0.68rem", height: 20, cursor: "pointer" }} />
                                </Tooltip>
                              )}
                              {qs.length === 0 && <span style={{ opacity: 0.4, fontSize: "0.75rem" }}>—</span>}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          const ws = user.whatsapps || [];
                          const visible = ws.slice(0, 2);
                          const extra = ws.length - 2;
                          return (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "center" }}>
                              {visible.map(w => (
                                <Chip key={w.id} label={w.name} size="small"
                                  style={{ backgroundColor: "#1976d2", color: "#fff", fontSize: "0.68rem", height: 20 }} />
                              ))}
                              {extra > 0 && (
                                <Tooltip title={ws.slice(2).map(w => w.name).join(", ")}>
                                  <Chip label={`+${extra}`} size="small"
                                    style={{ fontSize: "0.68rem", height: 20, cursor: "pointer" }} />
                                </Tooltip>
                              )}
                              {ws.length === 0 && <span style={{ opacity: 0.4, fontSize: "0.75rem" }}>—</span>}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleCreateChat(user)}
                          disabled={!showInternalChat}
                        >
                          <ChatBubbleOutlineIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openTransferTicketsModal(user)}
                          title="Transferir tickets"
                        >
                          <SwapHorizIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEditUser(user)}
                        >
                          <EditIcon />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={() => openDeleteModal(user)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                        ))}
                      </React.Fragment>
                    ));
                  })()}
                  {loadingMore && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              </TableBody>
            </Table>
            {loading && !loadingMore && (
              <div className={classes.loadingContainer}>
                <CircularProgress />
                <span className={classes.loadingText}>{i18n.t("loading")}</span>
              </div>
            )}
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default Users;
